import { HOME } from "@/constants/testIds";

export default function Hero() {
  return (
    <section
      id="home"
      className="relative bg-cocoa-900 overflow-hidden"
    >
      {/* Solid cocoa base */}
      <div className="absolute inset-0 bg-cocoa-900" />

      {/* Classical Saint Francis backdrop — Zurbarán, "Saint Francis in Meditation" 1639
          (public domain, National Gallery, London). Full-bleed, sepia-toned, dimmed
          to live as an atmospheric background while keeping all text & sections intact. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-[0.55] [filter:sepia(0.45)_saturate(0.75)_contrast(1.05)_brightness(0.85)]"
        style={{
          backgroundImage: "url('/saints/zurbaran-st-francis.jpg')",
          backgroundPosition: "62% 30%",
        }}
      />

      {/* Cocoa wash so text remains beautifully legible */}
      <div className="absolute inset-0 bg-gradient-to-r from-cocoa-900/95 via-cocoa-900/70 to-cocoa-900/35" />
      <div className="absolute inset-0 bg-gradient-to-b from-cocoa-900/40 via-transparent to-cocoa-900/75" />

      {/* Warm gold + terracotta atmospheric glows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_25%,rgba(212,175,55,0.20),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_90%,rgba(194,89,83,0.14),transparent_55%)]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 lg:pt-28 pb-20 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Text column */}
          <div className="lg:col-span-7 animate-fade-up">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-12 bg-gold/70" />
              <span className="text-gold text-[11px] uppercase tracking-[0.32em] font-sans">
                Pax et Bonum · Peace and All Good
              </span>
            </div>

            <h1 className="font-serif text-parchment-100 leading-[1.05] text-[clamp(2.4rem,5.5vw,5rem)] font-normal">
              A parish of
              <span className="block italic text-gold/95 mt-2">faith, mission &amp; hope.</span>
            </h1>

            <p className="mt-7 max-w-xl text-parchment-100/90 font-sans text-base md:text-lg leading-relaxed font-light">
              A Catholic community in Brgy. Mabuhay, General Santos City &mdash;
              journeying together in worship, service, formation, and synodal mission
              under the patronage of Saint Francis of Assisi.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="#schedule"
                data-testid={HOME.heroCtaSchedule}
                className="group inline-flex items-center gap-3 bg-parchment-100 text-cocoa-900 px-7 py-3.5 font-sans font-medium text-sm tracking-wide hover:bg-gold hover:text-cocoa-900 transition-all duration-300 shadow-xl"
              >
                View Mass Schedule
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#construction"
                data-testid={HOME.heroCtaConstruction}
                className="inline-flex items-center gap-3 border border-parchment-100/80 text-parchment-100 px-7 py-3.5 font-sans font-medium text-sm tracking-wide hover:bg-parchment-100 hover:text-cocoa-900 transition-all duration-300"
              >
                Support the Church Build
              </a>
            </div>

            {/* Sundays mini card under text on desktop */}
            <div className="mt-10 hidden md:inline-flex items-baseline gap-6 border-t border-parchment-100/20 pt-6 text-parchment-100/85 font-sans text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-1">Sundays</div>
                <div className="font-serif text-2xl text-parchment-100">6:00 · 8:00 · 15:00 · 17:00</div>
              </div>
              <div className="border-l border-parchment-100/30 pl-6 italic font-serif text-parchment-100/90 leading-snug max-w-xs">
                &ldquo;Lord, make me an instrument of your peace.&rdquo;
                <div className="not-italic text-[10px] uppercase tracking-[0.24em] text-parchment-100/70 mt-1">
                  — St. Francis of Assisi
                </div>
              </div>
            </div>
          </div>

          {/* Image column — Saint Francis embracing the crucified Christ (watercolor).
              Framed foreground element that anchors the first page. */}
          <div className="lg:col-span-5">
            <figure className="relative mx-auto w-full max-w-[440px]">
              {/* Warm halo behind the image */}
              <div
                aria-hidden
                className="absolute -inset-6 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.28),transparent_70%)] pointer-events-none"
              />
              <div className="relative border border-parchment-100/25 bg-cocoa-900 p-3 shadow-2xl">
                <div className="relative aspect-[2/3] overflow-hidden">
                  <img
                    src="/saints/st-francis-embrace.jpg"
                    alt="Saint Francis of Assisi embracing the crucified Christ — watercolor"
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                  />
                </div>
              </div>
              <figcaption className="mt-4 text-center text-[10px] uppercase tracking-[0.28em] text-gold/85 font-sans">
                ✦ Saint Francis of Assisi · Patron of the Parish ✦
              </figcaption>
            </figure>
          </div>
        </div>
      </div>

      {/* bottom scroll cue */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-parchment-100/60">
        <span className="text-[10px] uppercase tracking-[0.3em] font-sans">Scroll</span>
        <span className="block h-10 w-px bg-parchment-100/40 animate-pulse" />
      </div>
    </section>
  );
}
